export const normalizeDisplayValue = (v) =>
  Array.isArray(v) ? v[0] || "" : v || "";

/**
 * Hidden lists may contain either plain strings (manual selections — a value
 * since the id-keying fix, a display name in older saved configs) or
 * {display, value} objects (dynamic Top-N/Bottom-N presets).
 *
 * Matching is PER ENTRY, which is the whole point:
 * - An entry that carries a `value` (an id) matches by that id ONLY. Its
 *   display must never become a match key — tag names are not unique
 *   ("Positive" exists under three sub-themes), and pooling displays meant
 *   hiding one small "Positive" also hid the #1-ranked one Top-N had chosen
 *   to keep.
 * - A bare-string legacy entry can't say which kind it is, so it stays loose:
 *   it matches an item's value OR display.
 */
export const buildHiddenMatcher = (hiddenList) => {
  const valueKeys = new Set(); // id-carrying entries: match by id only
  const looseKeys = new Set(); // legacy strings (and valueless objects): match value or display
  (hiddenList || []).forEach((h) => {
    if (h && typeof h === "object") {
      if (h.value !== undefined && h.value !== null && h.value !== "") {
        valueKeys.add(String(h.value));
      } else if (h.display) {
        looseKeys.add(h.display);
      }
    } else if (h !== undefined && h !== null && h !== "") {
      looseKeys.add(h);
    }
  });
  return { valueKeys, looseKeys };
};

export const isHiddenDatapoint = (item, matcher) => {
  const rawValue = Array.isArray(item.value) ? item.value[0] : item.value;
  if (rawValue !== undefined && rawValue !== null && rawValue !== "") {
    const value = String(rawValue);
    if (matcher.valueKeys.has(value) || matcher.looseKeys.has(value)) return true;
  }
  const display = normalizeDisplayValue(item.display);
  if (display && matcher.looseKeys.has(display)) return true;
  return false;
};

export const filterByHiddenDatapoints = (data, hiddenList) => {
  if (!hiddenList?.length) return data;
  const matcher = buildHiddenMatcher(hiddenList);
  return data.filter((d) => !isHiddenDatapoint(d, matcher));
};

// A group whose children are all hidden stays (as a zero row) unless the
// widget's "Hide Zero Values" is on.
export const filterChildrenByHiddenDatapoints = (data, hiddenList, { keepEmptyGroups = true } = {}) => {
  if (!hiddenList?.length) return data;
  const matcher = buildHiddenMatcher(hiddenList);
  const filtered = data.map((d) => ({
    ...d,
    children: d.children?.filter((child) => !isHiddenDatapoint(child, matcher)),
  }));
  return keepEmptyGroups ? filtered : filtered.filter((d) => !d.children || d.children.length > 0);
};
